import { ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@repo/ui/components/dialog';
import { track } from '@/lib/analytics';
import { formatPercent } from '@/lib/format/percent/percent';

import type { HygieneCompleted } from '../types/hygiene';
import { HEALTH_REFERENCE } from '@/lib/health-reference';
import type { HealthAssessment } from '../utils/derive-health';
import {
  checkTitle,
  humanizeReference,
  signalName,
  signalStatusLabel
} from '../utils/hygiene-presentation';

export function MethodologyDialog({
  assessment,
  hygiene
}: {
  assessment: HealthAssessment;
  hygiene?: HygieneCompleted;
}) {
  // The two sources follow different rules. The gates and reference list
  // below the fold belong to the in-app interim only; beside the canonical
  // score they would describe rules that do not apply to it.
  const isCanonical = assessment.source === 'canonical';
  const guidelines = (hygiene?.categories ?? []).flatMap((category) =>
    category.checks
      .filter((check) => check.status !== 'not_applicable')
      .map((check) => ({
        id: `${category.id}:${check.id}`,
        title: checkTitle(check),
        guideline: humanizeReference(check.reference.display)
      }))
  );

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="text-rowa-text-brand text-rowa-label-md inline-flex items-center gap-1 font-medium hover:opacity-80"
          onClick={() => track('overview.health_methodology_opened')}
        >
          How health is calculated
          <ArrowRight className="size-3.5" aria-hidden />
        </button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>How portfolio health is calculated</DialogTitle>
          <DialogDescription>
            An advisory score worked out from your live portfolio data against
            the published ROWA guidelines. It never moves funds and is not a
            compliance or mandate result.
          </DialogDescription>
        </DialogHeader>
        <div className="text-rowa-body-sm text-rowa-text-secondary space-y-3">
          {isCanonical ? (
            <p>
              The score is built from {assessment.drivers.length} areas, each
              counting for the share shown below. A check without current data
              is left out of the math. It lowers how much of the scorecard has
              data instead of hurting your score.
            </p>
          ) : (
            <p>
              The score is built from {assessment.drivers.length} areas totaling
              100 points. A check without current data is left out of the math.
              It lowers how much of the scorecard has data instead of hurting
              your score. The overall score appears once{' '}
              {formatPercent(HEALTH_REFERENCE.minCoverageBps, {
                from: 'bps',
                digits: 0
              })}{' '}
              of the scorecard has data, and counts as early below{' '}
              {formatPercent(HEALTH_REFERENCE.provisionalCoverageBps, {
                from: 'bps',
                digits: 0
              })}
              .
            </p>
          )}
          <ul className="space-y-2">
            {assessment.drivers.map((entry) => (
              <li
                key={entry.id}
                className="flex items-baseline justify-between gap-3"
              >
                <span className="text-rowa-text-primary font-medium">
                  {entry.label}
                </span>
                <span>{entry.weight}% of the score</span>
              </li>
            ))}
          </ul>
          {isCanonical ? (
            guidelines.length > 0 ? (
              <div>
                <p className="text-rowa-text-primary font-medium">
                  The guideline behind each check
                </p>
                <ul className="mt-1.5 space-y-1">
                  {guidelines.map((entry) => (
                    <li
                      key={entry.id}
                      className="flex items-baseline justify-between gap-3"
                    >
                      <span>{entry.title}</span>
                      <span className="text-rowa-text-tertiary text-right">
                        {entry.guideline}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null
          ) : (
            <p>
              Guidelines: your largest underlying asset under{' '}
              {formatPercent(HEALTH_REFERENCE.largestAssetReferenceBps, {
                from: 'bps',
                digits: 0
              })}
              , a cash reserve near{' '}
              {formatPercent(HEALTH_REFERENCE.idleReserveShare, {
                from: 'fraction',
                digits: 0
              })}{' '}
              of your portfolio, at least{' '}
              {formatPercent(HEALTH_REFERENCE.oneDayLiquidityReferenceBps, {
                from: 'bps',
                digits: 0
              })}{' '}
              that you can sell within one day, and a known price and asset type
              for each holding.
            </p>
          )}
          <p>
            {isCanonical
              ? `This assessment was computed by the ROWA hygiene service (${assessment.attribution}). Scores, coverage, caps, and suggested actions come from the canonical assessment with full evidence provenance. The app never recomputes them.`
              : 'This score is worked out in the app while the full ROWA health check is not available here yet. It rates how your portfolio is set up, not how it has performed. Your drawdown and volatility are results, so they appear on Risk & liquidity and never move this score. Diversification measures how evenly your money is spread across the assets inside your vaults. Liquidity uses how quickly each holding can be sold, as shown on Risk & liquidity. When the full health check is available, it replaces this score.'}
          </p>
          {assessment.attribution ? (
            <p className="text-rowa-label-sm text-rowa-text-tertiary">
              {assessment.attribution}
            </p>
          ) : null}
          {assessment.confidence ? (
            <div>
              <p className="text-rowa-text-primary font-medium">Data signals</p>
              <ul className="mt-1.5 space-y-1">
                {assessment.confidence.signals.map((signal) => (
                  <li
                    key={signal.id}
                    className="flex items-baseline justify-between gap-3"
                  >
                    <span>{signalName(signal.id)}</span>
                    <span className="text-rowa-text-tertiary">
                      {signalStatusLabel(signal.status)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          {assessment.evidenceDetails &&
          assessment.evidenceDetails.length > 0 ? (
            <div>
              <p className="text-rowa-text-primary font-medium">
                For the record: the exact system messages behind the waiting
                states
              </p>
              <ul className="mt-1.5 list-disc space-y-1 pl-4">
                {assessment.evidenceDetails.map((detail, index) => (
                  // Two warnings can carry the same message.
                  <li key={index}>{detail}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
